import { defineNuxtRouteMiddleware, navigateTo, useError } from "nuxt/app";
import { getQueryParamValue } from "@chatfood/core-utils";
import { usePinia } from "#imports";
import { useBusinessStore } from "~/stores/business";
import { useMenusStore } from "~/stores/menus";
import { useOutletStore } from "~/stores/outlet";
import { withoutItemQuery } from "~/composables/use-navigation";

/**
 * Keeps the menus in sync with the customer's fulfillment time, then makes sure
 * the route points at a menu that is available for it.
 *
 * Runs after `ensure-basket-is-loaded`: the fulfillment time lives on the basket,
 * so menus resolved before it loads are resolved for "now" even when the customer
 * has a scheduled slot.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  if (useError().value) return;

  const pinia = usePinia();
  const business = useBusinessStore(pinia);
  const outlet = useOutletStore(pinia);
  const menus = useMenusStore(pinia);

  const orderingMode = outlet.activeOrderingMode;

  if (!outlet.isLoaded || !orderingMode) return;

  const menusQuery = {
    businessId: business.id,
    outletId: outlet.id,
    orderingMode,
    salesChannel: business.salesChannel,
  };

  if (menus.isResolvedForQuery(menusQuery)) {
    // Revalidate without holding up navigation. The menu page corrects the route
    // from its own watcher if the refresh drops the menu being viewed.
    if (import.meta.client) void menus.loadMenus(menusQuery);
  } else {
    await menus.loadMenus(menusQuery);
  }

  const menuSlug = getQueryParamValue(to.params, "menuSlug");

  if (!menuSlug) return;

  // The list failed to load — let the page show its empty state rather than
  // redirect on incomplete information.
  if (!menus.isLoaded) return;

  if (menus.isMenuAvailable(menuSlug)) return;

  menus.patch({ hasUnavailableMenuNotice: true });

  // The outlet page renders the only available menu, or the menu selection when
  // there are several — one destination covers both.
  return navigateTo(
    {
      name: "outlet",
      params: {
        orderingMode: to.params.orderingMode,
        outletSlug: to.params.outletSlug,
      },
      query: withoutItemQuery(to.query),
    },
    { replace: true },
  );
});
